import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { KsefCertificateService, KsefCertificateUploadErrorMessages } from '@skylab-angular-shared/business';
import { L10n } from '@skylab-angular-shared/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import {
    ButtonComponent,
    DialogButtonResult,
    DialogContentComponentBase,
    DialogContentComponentIns,
    DialogContentViewModel,
    DialogHeaderComponentIns,
    DialogInsService,
    DialogState,
    FileSizeUnit,
    FileUploadAreaComponent,
    PasswordComponentIns,
    ToastService,
    ValidationResult,
    ValidationSourceDirective,
    IconComponent
} from '@skylab-angular-shared/ui';
import { SkylabConfig } from '@skylab-shared/business';
import { L10nBase, ObservableCollection, VeryLongLength, ViolationSeverity } from '@skylab-shared/core';
import { HttpStatusCode } from 'axios';
import { ViewValidationService } from '../../form/form-validation';

@Component({
	selector: 'skl-ksef-certificate-dialog',
	template: `
		<skl-dialog-content-ins>
			<skl-dialog-header-ins
				[title]="'scenes.shared.ksef-certificate.dialog.title' | translate"
				(onClose)="onCancel()"
			/>
			<ins-content>
				<div class="dialog-body" [sklValidationSource]="viewValidation">
					<!-- Info Box -->
					<div class="message-box-info">
						<skl-icon name="info" size="16px" />
						<div>
							<h2>{{ 'scenes.shared.ksef-certificate.dialog.info-box.title' | translate }}</h2>
							<p>
								{{ 'scenes.shared.ksef-certificate.dialog.info-box.text' | translate }}
							</p>
						</div>
					</div>

					<!-- Step 1 -->
					<div class="step-section">
						<div class="step-header">
							<skl-icon name="cert" class="blue-icon" size="16px" />
							<span class="step-title">{{ 'scenes.shared.ksef-certificate.dialog.step-certificate.title' | translate }}<span class="required">*</span></span>
						</div>

						<div>
							<skl-file-upload-area [accept]="authCertAccept()" [maxFileSize]="maxFileSize()" (fileChange)="onCertChange($event!)"  [errorMessage]="getErrorMessage('certificateFile')">
								<ng-container slot="file-upload">
									<skl-icon name="document-arrown-up" class="upload-icon" size="32px" />
									<span class="message">{{ 'scenes.shared.ksef-certificate.dialog.step-certificate.placeholder' | translate }}</span>
								</ng-container>
							</skl-file-upload-area>

							<div class="help-link">
								<skl-icon name="help-circle" size="16px" />
								<a [href]="HELP_LINK" target="_blank" rel="noopener noreferrer" class="link-text">{{ 'scenes.shared.ksef-certificate.dialog.step-certificate.help-link' | translate }}</a>
							</div>
						</div>
					</div>

					<!-- Step 2 -->
					<div class="step-section">
						<div class="step-header">
							<skl-icon name="key" class="purple-icon" size="16px" />
							<span class="step-title">{{ 'scenes.shared.ksef-certificate.dialog.step-key.title' | translate }}<span class="required">*</span></span>
						</div>

						<div>
							<div class="message-box-warning">
								<skl-icon name="lock" size="16px" />
								<div>
									<span class="warning-highlight">{{ 'scenes.shared.ksef-certificate.dialog.step-key.warning-title' | translate }}:</span> {{ 'scenes.shared.ksef-certificate.dialog.step-key.warning-text' | translate }}
								</div>
							</div>

							<skl-file-upload-area [accept]="pvtKeyAccept()" [maxFileSize]="maxFileSize()" (fileChange)="onKeyChange($event!)" [errorMessage]="getErrorMessage('privateKeyFile')">
								<ng-container slot="file-upload">
									<skl-icon name="shield" size="32px" class="upload-icon" />
									<span class="message">{{ 'scenes.shared.ksef-certificate.dialog.step-key.placeholder' | translate }}</span>
								</ng-container>
							</skl-file-upload-area>
						</div>
					</div>

					<!-- Password Section -->
					<div class="form-section">
						<ins-form-field class="no-boundary">
							<div class="step-header">
								<span class="step-title">{{ 'scenes.shared.ksef-certificate.dialog.step-password.title' | translate }}<span class="required">*</span></span>
							</div>
							<skl-password-ins
								(insInput)="passwordChange($event)"
								[validationKey]="'privateKeyPassword.' + validationSun"
								placeholder="{{ 'scenes.shared.ksef-certificate.dialog.step-password.placeholder' | translate }}"
							/>
							<div slot="subscript">
								<div class="field-hint">
									<skl-icon name="info" size="14px" />
									<span>{{ 'scenes.shared.ksef-certificate.dialog.step-password.hint-text' | translate }}</span>
								</div>
							</div>
						</ins-form-field>
					</div>
				</div>
			</ins-content>

			<ins-footer>
				<div class="required-message">
					<span class="required">*</span> {{ 'scenes.shared.ksef-certificate.dialog.required' | translate }}
				</div>
				<div class="footer-buttons">
					<skl-button
						typeName="accent"
						[disabled]="!isFormValid()"
						[spinner]="isFormSaving()"
						(click)="onConfirmAsync()">
						{{ 'scenes.shared.ksef-certificate.dialog.upload-button' | translate }}
					</skl-button>
					<skl-button
						(click)="onCancel()"
					>
						{{ 'scenes.shared.ksef-certificate.dialog.cancel-button' | translate }}
					</skl-button>
				</div>
			</ins-footer>
		</skl-dialog-content-ins>
	`,
	styles: `
		:host {
			--bg-dialog: light-dark(oklch(100% 0 0), oklch(26.6% 0.005 264));
			--text-primary: light-dark(oklch(20.8% 0.005 264), oklch(92.9% 0.006 264));
			--text-secondary: light-dark(oklch(33.8% 0.006 264), oklch(79.3% 0.006 264));
			--text-tertiary: light-dark(oklch(46.3% 0.007 264), oklch(68.3% 0.007 264));
			--border-color: light-dark(oklch(79.3% 0.006 264), oklch(46.3% 0.007 264));

			--accent-blue: light-dark(oklch(56.3% 0.18 264), oklch(76.3% 0.11 264));
			--accent-purple: light-dark(oklch(56.3% 0.22 300), oklch(76.3% 0.14 300));
			--success-color: light-dark(oklch(52.3% 0.14 142), oklch(76.3% 0.09 142));
			--success-bg: light-dark(oklch(94.6% 0.02 142), oklch(25.3% 0.06 142));

			--input-bg: light-dark(oklch(100% 0 0), oklch(20.8% 0.005 264));
			--input-border-focus: light-dark(oklch(56.3% 0.18 264), oklch(76.3% 0.11 264));
			--input-icon-color: light-dark(oklch(46.3% 0.007 264), oklch(68.3% 0.007 264));
			--input-icon-color-hover: light-dark(oklch(20.8% 0.005 264), oklch(92.9% 0.006 264));
		}

		[class|="message-box"] {
			margin-block-start: var(--gutter-small);
			margin-block-end: var(--gutter-medium);
		}

		.dialog-body {
			display: flex;
			flex-direction: column;
			gap: var(--gutter-medium);
			padding-block: var(--gutter-small);
			padding-inline: var(--gutter-medium);
		}

		.step-section {
			display: flex;
			flex-direction: column;
			gap: var(--gutter-small);
		}

		.step-header {
			display: flex;
			align-items: center;
			gap: var(--gutter-small);
		}

		.step-title {
			font-size: 15px;
			font-weight: 600;
			color: var(--text-minor-color);
		}

		.required-message {
			font-size: .875em;
			color: var(--text-tertiary);
			white-space: nowrap;
			align-self: center;
		}

		.required {
			color: var(--error-color);
			margin-left: 2px;
		}

		.blue-icon { color: var(--accent-blue); }
		.purple-icon { color: var(--accent-purple); }

		.help-link {
			display: flex;
			align-items: center;
			gap: 6px;
			margin-top: 8px;
			color: var(--text-tertiary);
			font-size: .75em;
		}

		.link-text {
			color: var(--text-tertiary);
			text-decoration: none;
			&:hover {
				color: var(--text-primary);
				text-decoration: underline;
			}
		}

		.warning-highlight { font-weight: 700; }

		.form-section { margin-top: 8px; }

		.field-hint {
			display: flex;
			align-items: center;
			gap: 6px;
			margin-top: 6px;
			font-size: 12px;
			color: var(--text-tertiary);
		}

		.footer-buttons {
			display: flex;
			justify-content: flex-end;
			gap: 12px;
			width: 100%;
		}
	`,
	imports: [
		FormsModule,
		DialogContentComponentIns,
		DialogHeaderComponentIns,
		ButtonComponent,
		PasswordComponentIns,
		ButtonComponent,
		FileUploadAreaComponent,
		TranslatePipe,
		ValidationSourceDirective,
		IconComponent
	],
	schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class KsefCertificateDialogComponent extends DialogContentComponentBase<DialogContentViewModel, any, any> {
	public readonly authCertAccept = signal('.crt');
	public readonly pvtKeyAccept = signal('.key');
	public readonly maxFileSize = signal({value: 1, unit: FileSizeUnit.MB});

	public readonly HELP_LINK = SkylabConfig.ksefCertificateInfo;

	public readonly certificateFile = model<File | null>(null);
	public readonly privateKeyFile = model<File | null>(null);
	public readonly privateKeyPassword = model<string | null>(null);

	private readonly fieldsValid = signal(true);
	public readonly isFormFilled = computed(() => !!this.certificateFile() && !!this.privateKeyFile() && !!this.privateKeyPassword());
	public readonly isFormValid = computed(() => this.isFormFilled() && this.fieldsValid());
	public readonly isFormSaving = signal(false);

	private readonly ksefCertificateService = inject(KsefCertificateService);
	private readonly l10n = inject(L10n) as L10nBase;
	private readonly toastService = inject(ToastService);
	private readonly viewValidationService = inject(ViewValidationService);
	private readonly validationSource = {validationResult: new ObservableCollection<ValidationResult>()};
	private readonly destroyRef = inject(DestroyRef);

	public readonly validationSun = 'ksef-certificate-dialog-context';

	public readonly viewValidation = this.viewValidationService.create([this.validationSource]);

	public constructor(
		dialogService: DialogInsService
	) {
		super(dialogService);
		const listener = this.validationSource.validationResult.onCollectionChanged(_ => {
			this.fieldsValid.set(!this.validationSource.validationResult.length);
		})
		this.destroyRef.onDestroy(() => {
			listener?.();
		});
	}

	public onCertChange(file: File): void {
		this.removeViolation('certificateFile');
		this.certificateFile.set(file);
	}

	public onKeyChange(file: File): void {
		this.removeViolation('privateKeyFile');
		this.privateKeyFile.set(file);
	}

	public passwordChange(e: Event): void {
		this.removeViolation('privateKeyPassword');
		this.privateKeyPassword.set((e.target as HTMLInputElement)?.value || '');
		this.validatePassword('empty');
		this.validatePassword('length');
	}

	public async onConfirmAsync(): Promise<void> {
		try {
			this.isFormSaving.set(true);
			await this.ksefCertificateService.uploadCertificateAsync({
				certificateFile: this.certificateFile()!,
				privateKeyFile: this.privateKeyFile()!,
				privateKeyPassword: this.privateKeyPassword()!
			})
			this.toastService.showAsync({
				type: 'success',
				text: this.l10n._T('scenes.shared.ksef-certificate.dialog.messages.success'),
			});
			this.dialogService.close({state: DialogState.Confirm});
		} catch (err) {
			this.handleSaveError(err as any);
		} finally {
			this.isFormSaving.set(false);
		}
	}

	private handleSaveError(err: any): void {
		const customCode = err?.data?.code;
		const conflict = err?.status === HttpStatusCode.Conflict;

		if (conflict || !customCode) {
			this.toastService.showAsync({
				type: 'error',
				text: this.l10n._T(`scenes.shared.ksef-certificate.dialog.messages.error.${conflict ? 'already-exists' : 'base'}`),
			});
			return;
		}

		const propName = ( {
			[KsefCertificateUploadErrorMessages.INCORRECT_PRIVATE_KEY_PASSWORD]: 'privateKeyPassword',
			[KsefCertificateUploadErrorMessages.CERT_FORMAT_INCORRECT]: 'certificateFile',
			[KsefCertificateUploadErrorMessages.CERT_IS_NOT_OFFLINE]: 'certificateFile',
			[KsefCertificateUploadErrorMessages.INVALID_PRIVATE_KEY]: 'privateKeyFile',
			[KsefCertificateUploadErrorMessages.KEY_DOES_NOT_MATCH_CERT]: 'privateKeyFile',
			[KsefCertificateUploadErrorMessages.UNSUPPORTED_PRIVATE_KEY_FORMAT]: 'privateKeyFile',
		} as Record<string, string> )[customCode];

		this.validationSource.validationResult.push(this.getErrorObject(propName, `scenes.shared.ksef-certificate.dialog.messages.error.ksef-certificate-error.${customCode}`));
	}

	public override onAction(data: DialogButtonResult): void {
		if (data.state !== DialogState.Confirm){
			super.onAction(data);
			return;
		}
		this.onConfirmAsync();
	}

	private validatePassword(type: 'length' | 'empty'): void {
		const val = this.privateKeyPassword();
		const validFunc: Record<typeof type, () => boolean> = {
			'length': () => !!(val && val.length > VeryLongLength),
			'empty': () => !val
		};

		if (!validFunc[type]()) return;

		this.validationSource.validationResult.push(this.getErrorObject('privateKeyPassword', `scenes.shared.ksef-certificate.dialog.step-password.errors.${type}`));
	}

	private getErrorObject(propName: string, message: string): ValidationResult {
		return {
			source: {
				sun: this.validationSun
			},
			propertyName: propName,
			ruleViolation: {
				violation: {
					severity: ViolationSeverity.Error,
					message: message
				}
			}
		}
	}

	private removeViolation(key: string): void {
		const toRemove = this.validationSource.validationResult.filter(elem => elem.propertyName === key);
		this.validationSource.validationResult.remove(...toRemove);
	}

	public getErrorMessage(key: string): string {
		const err =  this.validationSource.validationResult.find(e => e.propertyName === key)?.ruleViolation.violation.message;
        if (!err) return '';
        return this.l10n._T(err);
	}
}
